import { sortOrganisations } from "@/utils/organisationSort";
import { useState, useEffect } from "react";
import { ShortTermCalculator } from "@/components/crm/ShortTermCalculator";
import { downloadShortTermQuotePdf } from "@/lib/shortTermQuotePdf";
import { shortTermQuote, SHORT_TERM_PACKAGES, type ShortTermMonths } from "@/lib/shortTermPricing";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, Camera, Image, Calculator, Shield, Search, User, Building, ShoppingCart, CreditCard, Download, FileCheck, Loader2, FlaskConical, Package, Lock, Gift, BrainCircuit } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useCreateDeal } from "@/hooks/use-deals";
import { useContacts } from "@/hooks/use-contacts";
import { useCompanies } from "@/hooks/use-companies";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DirectDealConversionService } from "@/services/directDealConversionService";
import { COMPANY_ADDRESS_LINES, COMPANY_NAME, COMPANY_PDF_FOOTER } from "@/constants/company";

interface CameraVariant {
  id: string;
  name: string;
  price: number;
  description: string;
  hidden?: boolean;
}

const cameraVariants: CameraVariant[] = [
  {
    id: "ssc100",
    name: "Day Vision SSC100",
    price: 195,
    description: "Standard day vision camera",
    hidden: true
  },
  {
    id: "ssc200",
    name: "Day & Night Vision Camera SSC200",
    price: 295,
    description: "Enhanced day & night vision camera"
  }
];

const visibleCameraVariants = cameraVariants.filter(c => !c.hidden);

interface PlanTier {
  name: string;
  imagesPerDay: number;
  imagesPerYear: number;
  pricePerImage: number;
  description: string;
  color: string;
}

const TRIAL_CREDITS = 600;
const TRIAL_CAMERA_PRICE = 495;

const planTiers: PlanTier[] = [
  {
    name: "Trial",
    imagesPerDay: 0,
    imagesPerYear: 600,
    pricePerImage: 0,
    description: "Camera + 600 credits, continue or return",
    color: "bg-slate-100 border-slate-300"
  },
  {
    name: "Core", 
    imagesPerDay: 3,
    imagesPerYear: 1095,
    pricePerImage: 0.39,
    description: "3 images per day – ideal for standard monitoring",
    color: "bg-teal-50 border-teal-300"
  },
  {
    name: "Pro",
    imagesPerDay: 6, 
    imagesPerYear: 2190,
    pricePerImage: 0.35,
    description: "6 images per day – enhanced visibility",
    color: "bg-yellow-50 border-yellow-300"
  },
  {
    name: "Plus",
    imagesPerDay: 8,
    imagesPerYear: 2920,
    pricePerImage: 0.29,
    description: "8 images per day – maximum coverage",
    color: "bg-emerald-50 border-emerald-300"
  }
];

// Map legacy plan names to new ones for backward compatibility
const legacyPlanMapping: Record<string, string> = {
  'Extended': 'Core',
  'Essential': 'Core',
  'Professional': 'Pro',
  'Freedom': 'Plus'
};

// Legacy packages — hidden by default, surfaced via "Show legacy packages" toggle
const legacyPlanTiers: PlanTier[] = [
  {
    name: "Extended",
    imagesPerDay: 2,
    imagesPerYear: 720,
    pricePerImage: 0.49,
    description: "Legacy package – 720 images/year",
    color: "bg-slate-100 border-slate-300"
  },
  {
    name: "1 Year Rolling",
    imagesPerDay: 5,
    imagesPerYear: 1825,
    pricePerImage: 0.35,
    description: "Legacy package – 1,825 images, 1 year rolling term",
    color: "bg-slate-100 border-slate-300"
  },
  {
    name: "1 Year Rolling 1095",
    imagesPerDay: 3,
    imagesPerYear: 1095,
    pricePerImage: 0.39,
    description: "Legacy package – 1,095 images, 1 year rolling term",
    color: "bg-slate-100 border-slate-300"
  }
];

const ROLLING_PLAN_NAMES = ["1 Year Rolling", "1 Year Rolling 1095"];

interface Accessory {
  id: string;
  name: string;
  price: number;
  description: string;
}

const accessories: Accessory[] = [
  {
    id: "portable_stand",
    name: "Portable Camera Stand (POR-001)",
    price: 70.53,
    description: "Portable tripod stand for temporary deployments"
  },
  {
    id: "magnetic_bracket",
    name: "Magnetic Bracket (MAG-001)",
    price: 44.63,
    description: "Magnetic mounting bracket for steel surfaces"
  },
  {
    id: "pole_mount_1_5m",
    name: "Pole Mount 1.5m (POL-001)",
    price: 118.43,
    description: "1.5m pole mount for elevated camera positioning"
  },
  {
    id: "charger",
    name: "Charger (CHAR-001)",
    price: 25.00,
    description: "Charger for SSC200"
  },
  {
    id: "telescopic_pole",
    name: "Telescopic Pole (TEL-001)",
    price: 195.00,
    description: "Extendable pole for elevated camera positioning"
  },
  {
    id: "cctv_plaque_bracket",
    name: "CCTV Plaque and Bracket (CCT-001)",
    price: 22.49,
    description: "Regulation sign for SSC200"
  },
  {
    id: "carry_case_4",
    name: "Carry Case for 4 Cameras (CAS-001)",
    price: 65.26,
    description: "Carry case for transporting up to 4 cameras"
  }
];

interface DealCalculatorProps {
  existingDeal?: any;
  onClose?: () => void;
}

export const DealCalculator = ({ existingDeal, onClose }: DealCalculatorProps = {}) => {
  const fmtGBP = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [open, setOpen] = useState(!!existingDeal);
  const [orderType, setOrderType] = useState<'cameras_and_credits' | 'credits_only'>('cameras_and_credits');
  const [selectedCamera, setSelectedCamera] = useState<CameraVariant>(visibleCameraVariants[0]);
  const [cameraQuantity, setCameraQuantity] = useState(1);
  const [credits, setCredits] = useState(1095);
  const [contactId, setContactId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [currentStaffMember, setCurrentStaffMember] = useState<any>(null);
  const [discountTypes, setDiscountTypes] = useState<string[]>([]);
  const [paymentType, setPaymentType] = useState<'annual' | 'upfront'>('annual');
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>(planTiers[0]);
  const [commitmentYears, setCommitmentYears] = useState<2 | 3 | 5>(3);
  const [showTwoYear, setShowTwoYear] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const { isSuperuser } = useRoleFlags();
  const [accessoryQuantities, setAccessoryQuantities] = useState<Record<string, number>>({});
  const [customAiModel, setCustomAiModel] = useState(false);
  const [termType, setTermType] = useState<'long' | 'short'>('long');
  const [shortTerm, setShortTerm] = useState<{ cameras: number; imagesPerDay: number; months: ShortTermMonths }>({ cameras: 1, imagesPerDay: 4, months: 3 });
  const [shortTermSaving, setShortTermSaving] = useState(false);
  
  // MSA Direct Conversion state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [customerPO, setCustomerPO] = useState("");
  const [isConverting, setIsConverting] = useState(false);

  const { data: hasCrmAccess } = useCrmAccess();
  const { user } = useAuth();
  const createDeal = useCreateDeal();
  const { data: contacts = [] } = useContacts();
  const { data: companies = [] } = useCompanies();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Check if user can edit discounts (Superusers and Partners only).
  // Partner status comes from the partner record, not a separate role.
  const { data: userRole, isLoading: isLoadingRole } = useQuery({
    queryKey: ['user-role'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      
      const { data: role, error } = await supabase.rpc('get_user_role', {
        user_id: session.user.id
      });
      
      if (error) {
        console.error('DealCalculator: Error fetching user role:', error);
        throw error;
      }
      
      return role;
    },
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });

  const { isPartner } = useRoleFlags();

  const canEditDiscount = userRole === 'superuser' || isPartner;

  // Get MSA status from existing deal's organization
  const dealOrg = existingDeal?.organization as { has_msa?: boolean; msa_reference?: string } | undefined;
  const hasMsa = dealOrg?.has_msa || false;
  const msaReference = dealOrg?.msa_reference || "";

  // Handle MSA Direct Conversion
  const handleDirectConversion = async () => {
    if (!existingDeal?.id || !customerPO.trim()) return;
    
    setIsConverting(true);
    try {
      const result = await DirectDealConversionService.convertDealToOrder(
        existingDeal.id, 
        customerPO.trim()
      );
      
      if (!result.success) {
        throw new Error(result.error || "Conversion failed");
      }
      
      toast({
        title: "Success",
        description: "Deal converted to order successfully!"
      });
      
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      
      setConvertDialogOpen(false);
      setCustomerPO("");
      handleClose(false);
    } catch (error: any) {
      console.error("Error converting deal:", error);
      toast({
        title: "Error",
        description: `Failed to convert deal: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsConverting(false);
    }
  };

  // Populate data from existing deal if provided
  useEffect(() => {
    if (existingDeal) {
      const dealData = existingDeal.deal_data || {};
      
      if (dealData.term_type === 'short' && dealData.short_term) {
        setTermType('short');
        setShortTerm({
          cameras: existingDeal.total_cameras || 1,
          imagesPerDay: dealData.short_term.images_per_day || 4,
          months: dealData.short_term.term_months === 12 ? 12 : 3,
        });
      }
      setOrderType(dealData.order_type || 'cameras_and_credits');
      setCameraQuantity(existingDeal.total_cameras || 1);
      setCredits(dealData.credits_per_camera || 1095);
      setContactId(existingDeal.contact_id || "");
      setOrganizationId(existingDeal.organization_id || "");
      // Backward compat: load old discount_type string as array
      if (dealData.discount_types && Array.isArray(dealData.discount_types)) {
        setDiscountTypes(dealData.discount_types);
      } else if (dealData.discount_type && dealData.discount_type !== 'none') {
        setDiscountTypes([dealData.discount_type]);
      } else {
        setDiscountTypes([]);
      }
      setPaymentType(dealData.payment_structure || "annual");
      
      // Restore commitment years
      if (dealData.commitment_years) {
        const cy = dealData.commitment_years;
        if (cy === 2) {
          setCommitmentYears(2);
          setShowTwoYear(true);
        } else {
          setCommitmentYears(cy === 5 ? 5 : 3);
        }
      } else if (dealData.coverage_years) {
        // Legacy: map old coverage_years to commitment
        setCommitmentYears(dealData.coverage_years >= 5 ? 5 : 3);
      }
      
      // Restore plan selection with legacy mapping
      if (dealData.plan === 'Trial') {
        setSelectedPlan(planTiers[0]);
      } else {
        // Check legacy tier first (e.g. saved Extended at 720 images/year)
        const legacyMatch = legacyPlanTiers.find(
          p => p.name === dealData.plan &&
               (!dealData.credits_per_camera || dealData.credits_per_camera === p.imagesPerYear)
        );
        if (legacyMatch) {
          setSelectedPlan(legacyMatch);
          setShowLegacy(true);
        } else {
          const planName = legacyPlanMapping[dealData.plan] || dealData.plan;
          const savedPlan = planTiers.find(p => p.name === planName);
          if (savedPlan) setSelectedPlan(savedPlan);
        }
      }
      
      // DIRECTLY set selectedContact from existingDeal if available
      if (existingDeal.contact) {
        setSelectedContact(existingDeal.contact);
      }
      
      // Restore camera selection from saved data
      if (dealData.camera_model_id) {
        const savedCamera = cameraVariants.find(c => c.id === dealData.camera_model_id);
        if (savedCamera) setSelectedCamera(savedCamera);
      } else if (dealData.camera_model) {
        const savedCamera = cameraVariants.find(c => c.name === dealData.camera_model);
        if (savedCamera) setSelectedCamera(savedCamera);
      }

      // Restore accessories
      if (dealData.accessories && Array.isArray(dealData.accessories)) {
        const restoredQty: Record<string, number> = {};
        dealData.accessories.forEach((acc: any) => {
          if (acc.id && acc.quantity > 0) restoredQty[acc.id] = acc.quantity;
        });
        setAccessoryQuantities(restoredQty);
      }

      // Restore custom AI model
      if (dealData.custom_ai_model) {
        setCustomAiModel(true);
      }
    }
  }, [existingDeal]);

  // Handle close with callback
  const handleClose = (newOpen: boolean) => {
    if (!newOpen) {
      if (onClose) onClose();
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  // Get current staff member on component mount
  useEffect(() => {
    const getCurrentStaffMember = async () => {
      if (!user?.email) return;
      try {
        const { data: staffRecord } = await supabase
          .from('crm_staff')
          .select('id, first_name, last_name, email')
          .eq('email', user.email)
          .maybeSingle();
        if (staffRecord) setCurrentStaffMember(staffRecord);
      } catch (error) {
        console.error('Error fetching current staff member:', error);
      }
    };
    getCurrentStaffMember();
  }, [user?.email]);

  if (!hasCrmAccess) return null;

  // Filter contacts based on search
  const filteredContacts = contacts.filter(contact => {
    const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
    const email = (contact.email || '').toLowerCase();
    const searchTerm = contactSearch.toLowerCase();
    return fullName.includes(searchTerm) || email.includes(searchTerm);
  });

  // Auto-populate organization when contact is selected
  useEffect(() => {
    if (contactId) {
      const contact = contacts.find(c => c.id === contactId);
      if (contact) {
        setSelectedContact(contact);
        setOrganizationId(contact.organization_id || "");
      }
    } else {
      setSelectedContact(null);
      setOrganizationId("");
    }
  }, [contactId, contacts]);

  // Business logic calculations
  const isTrial = selectedPlan.name === 'Trial';
  const isRolling = ROLLING_PLAN_NAMES.includes(selectedPlan.name);

  const handlePlanSelect = (plan: PlanTier) => {
    setSelectedPlan(plan);
    if (plan.name !== 'Trial') {
      setCredits(plan.imagesPerYear);
    }
    if (ROLLING_PLAN_NAMES.includes(plan.name)) {
      setPaymentType('annual');
    }
    toast({
      title: `${plan.name} Plan Selected!`,
      description: plan.description,
    });
  };

  // Camera is free with 5-year commitment (non-trial only)
  const isFiveYearFree = !isRolling && commitmentYears === 5;
  const cameraPrice = isTrial ? TRIAL_CAMERA_PRICE : (isFiveYearFree ? 0 : selectedCamera.price);
  const effectiveCameraQuantity = orderType === 'cameras_and_credits' ? cameraQuantity : 0;
  
  // Trial overrides
  const effectiveCredits = isTrial ? TRIAL_CREDITS : credits;
  const pricePerImage = isTrial ? 0 : selectedPlan.pricePerImage + (customAiModel ? 0.10 : 0);
  
  const totalCreditsNeeded = isTrial
    ? TRIAL_CREDITS * (orderType === 'cameras_and_credits' ? cameraQuantity : 1)
    : (orderType === 'cameras_and_credits' ? credits * cameraQuantity : credits);
  
  const coverageYears = (isTrial || isRolling) ? 1 : commitmentYears;
  const creditsCost = isTrial ? 0 : totalCreditsNeeded * pricePerImage;
  const cameraCost = effectiveCameraQuantity * cameraPrice;
  
  // Annual contract calculations
  const totalCreditsOverContract = totalCreditsNeeded * coverageYears;
  const annualCredits = totalCreditsNeeded; // per year = credits per camera * cameras
  const annualCreditsCost = annualCredits * pricePerImage;
  const year1Subtotal = cameraCost + annualCreditsCost;
  
  // Per-line discounts: image discount applies to ALL years; surfaced inline on price lines
  // Image discounts are mutually exclusive; pick the largest selected value.
  const imageDiscountPerImage = isTrial
    ? 0
    : discountTypes.includes('image_14p')
      ? 0.14
      : discountTypes.includes('image_10p')
        ? 0.10
        : discountTypes.includes('image_4p')
          ? 0.04
          : discountTypes.includes('image_2p')
            ? 0.02
            : 0;
  const hasImageDiscount = imageDiscountPerImage > 0;
  const cameraDiscountAmount = isTrial
    ? 0
    : discountTypes.includes('camera_100')
      ? cameraCost
      : discountTypes.includes('camera_50')
        ? cameraCost * 0.5
        : discountTypes.includes('camera_10')
          ? effectiveCameraQuantity * 10
          : 0;
  const cameraDiscountLabel = discountTypes.includes('camera_100')
    ? 'Camera Discount (100%)'
    : discountTypes.includes('camera_50')
      ? 'Camera Discount (50%)'
      : discountTypes.includes('camera_10')
        ? 'Camera Discount (£10/cam)'
        : '';
  const cameraCostNet = cameraCost - cameraDiscountAmount;
  const discountedPricePerImage = hasImageDiscount ? Math.max(0, pricePerImage - imageDiscountPerImage) : pricePerImage;
  const annualCreditsCostNet = annualCredits * discountedPricePerImage;
  const year1ImageDiscount = hasImageDiscount ? annualCredits * imageDiscountPerImage : 0;
  const fullImageDiscount = hasImageDiscount ? annualCredits * imageDiscountPerImage * coverageYears : 0;
  const trialDiscountAmt = isTrial && discountTypes.includes('essential_100') ? cameraCost : 0;

  // Accessories cost (one-time) and optional 100% discount
  const accessoriesCost = Object.entries(accessoryQuantities).reduce((acc, [id, qty]) => {
    const item = accessories.find(a => a.id === id);
    return acc + (qty * (item?.price || 0));
  }, 0);
  const accessoriesDiscountAmount = discountTypes.includes('accessories_100') ? accessoriesCost : 0;
  const accessoriesCostNet = accessoriesCost - accessoriesDiscountAmount;
  const accessoriesDiscountLabel = accessoriesDiscountAmount > 0 ? '100% off Accessories' : '';

  // Total contract savings (used for storage / reporting)
  const discountAmount = isTrial
    ? trialDiscountAmt + accessoriesDiscountAmount
    : cameraDiscountAmount + fullImageDiscount + accessoriesDiscountAmount;

  const subtotal = isTrial
    ? (cameraCost + accessoriesCost)
    : (paymentType === 'annual'
      ? (year1Subtotal + accessoriesCost)
      : (creditsCost * coverageYears + cameraCost + accessoriesCost));
  // `total` is the year-1 payment in annual mode (subtracts only year-1 image discount),
  // and the full upfront payment in upfront mode (subtracts full multi-year image discount).
  const total = isTrial
    ? subtotal - trialDiscountAmt - accessoriesDiscountAmount
    : paymentType === 'annual'
      ? (year1Subtotal + accessoriesCostNet - cameraDiscountAmount - year1ImageDiscount)
      : (creditsCost * coverageYears + cameraCost + accessoriesCostNet - cameraDiscountAmount - fullImageDiscount);

  const totalDays = 365 * coverageYears;
  const shotsPerDay = isTrial ? Math.floor(TRIAL_CREDITS / 365) : Math.round(credits / 365);
  const imageDiscountOverContract = fullImageDiscount;
  const trialDiscount = discountTypes.includes('essential_100') ? cameraCost : 0;
  const totalContractCost = isTrial ? (cameraCost + accessoriesCostNet - trialDiscount) : (cameraCost + accessoriesCostNet + (annualCreditsCost * coverageYears) - imageDiscountOverContract);
  const averageCostPerDay = totalContractCost / totalDays / Math.max(1, effectiveCameraQuantity || 1);
  const averageCostPerWeek = averageCostPerDay * 7;
  const averageCostPerMonth = averageCostPerDay * 30;
  const isFleetMode = cameraQuantity >= 25;

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, cameraQuantity + delta);
    setCameraQuantity(newQuantity);
  };

  const handleCreditsChange = (value: number[]) => {
    const newCredits = value[0];
    setCredits(newCredits);
  };

  const handleContactSelect = (value: string) => {
    setContactId(value);
    setContactSearch("");
  };

  const handleCreateDeal = async () => {
    try {
      if (!organizationId) {
        toast({ title: "Validation Error", description: "Organization is required to create an opportunity.", variant: "destructive" });
        return;
      }
      if (!contactId) {
        toast({ title: "Validation Error", description: "Contact person is required to create an opportunity.", variant: "destructive" });
        return;
      }

      let assigneeInfo = {
        assigneeName: 'System',
        assigneeType: 'Staff',
        assigneeEmail: user?.email || 'system@snapshot.live'
      };

      if (currentStaffMember) {
        assigneeInfo = {
          assigneeName: `${currentStaffMember.first_name} ${currentStaffMember.last_name}`,
          assigneeType: 'Staff',
          assigneeEmail: currentStaffMember.email
        };
      } else if (user?.email) {
        assigneeInfo = {
          assigneeName: user.email.split('@')[0],
          assigneeType: 'Staff',
          assigneeEmail: user.email
        };
      }

      const { data: stages } = await supabase
        .from('crm_deal_stages')
        .select('id, name')
        .eq('name', 'Opportunity')
        .maybeSingle();

      const getOrderTitle = () => {
        if (isTrial) {
          if (orderType === 'credits_only') return `${totalCreditsNeeded.toLocaleString()} Credits (Trial)`;
          return `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (Trial)`;
        }
        if (orderType === 'credits_only') return `${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
        return `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
      };

      const dealData = {
        title: getOrderTitle(),
        contact_id: contactId || undefined,
        organization_id: organizationId || undefined,
        value: total,
        discount_percentage: 0,
        probability: 50,
        stage_id: stages?.id || undefined,
        total_cameras: effectiveCameraQuantity,
        total_images: totalCreditsNeeded,
        total_revenue: total,
        image_revenue: isTrial ? 0 : annualCreditsCost,
        assigned_to_staff_id: currentStaffMember?.id || undefined,
        deal_data: {
          order_type: orderType,
          payment_structure: isTrial ? 'upfront' : paymentType,
          camera_model: selectedCamera.name,
          camera_model_id: selectedCamera.id,
          camera_unit_price: cameraPrice,
          plan: selectedPlan.name,
          credits_per_camera: isTrial ? TRIAL_CREDITS : credits,
          price_per_image: pricePerImage,
          camera_cost: cameraCost,
          credits_cost: isTrial ? 0 : annualCreditsCostNet,
          discount_amount: discountAmount,
          discount_type: discountTypes.length > 0 ? discountTypes[0] : 'none',
          discount_types: discountTypes,
          coverage_years: coverageYears,
          commitment_years: (isTrial || isRolling) ? 1 : commitmentYears,
          shots_per_day: shotsPerDay,
          images_per_day: isTrial ? 0 : selectedPlan.imagesPerDay,
          annual_payment_breakdown: paymentType === 'annual' ? {
            year1_payment: total,
            subsequent_annual_payment: annualCreditsCostNet,
            contract_years: coverageYears,
            annual_credits: annualCredits
          } : null,
          assigneeName: assigneeInfo.assigneeName,
          assigneeType: assigneeInfo.assigneeType,
          assigneeEmail: assigneeInfo.assigneeEmail,
          customerGenerated: false,
          dealType: 'proposal',
          accessories: Object.entries(accessoryQuantities)
            .filter(([, qty]) => qty > 0)
            .map(([id, qty]) => {
              const item = accessories.find(a => a.id === id);
              return { id, name: item?.name || id, quantity: qty, unit_price: item?.price || 0 };
            }),
          accessories_cost: accessoriesCost,
          custom_ai_model: customAiModel
        }
      };

      await createDeal.mutateAsync(dealData);
      
      if (!existingDeal) {
        setCameraQuantity(1);
        setCredits(1095);
        setContactId("");
        setOrganizationId("");
        setContactSearch("");
        setSelectedContact(null);
        setDiscountTypes([]);
        setPaymentType("annual");
        setSelectedPlan(planTiers[0]);
        setCommitmentYears(3);
        setAccessoryQuantities({});
        setCustomAiModel(false);
      }
      
      setOpen(false);
    } catch (error) {
      console.error("❌ [DealCalculator] Error creating opportunity:", error);
      toast({ title: "Error", description: "Failed to create opportunity. Please try again.", variant: "destructive" });
    }
  };

  const handleUpdateDeal = async () => {
    try {
      if (!organizationId) {
        toast({ title: "Validation Error", description: "Organization is required to update the opportunity.", variant: "destructive" });
        return;
      }
      if (!contactId) {
        toast({ title: "Validation Error", description: "Contact person is required.", variant: "destructive" });
        return;
      }
      if (!existingDeal?.id) throw new Error('No existing deal to update');

      const getOrderTitle = () => {
        if (isTrial) {
          if (orderType === 'credits_only') return `${totalCreditsNeeded.toLocaleString()} Credits (Trial)`;
          return `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (Trial)`;
        }
        if (orderType === 'credits_only') return `${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
        return `${effectiveCameraQuantity}x Camera${effectiveCameraQuantity > 1 ? 's' : ''} + ${totalCreditsNeeded.toLocaleString()} Credits (${selectedPlan.name} Plan)`;
      };

      const updateData = {
        title: getOrderTitle(),
        contact_id: contactId || undefined,
        organization_id: organizationId || undefined,
        value: total,
        total_cameras: effectiveCameraQuantity,
        total_images: totalCreditsNeeded,
        total_revenue: total,
        image_revenue: isTrial ? 0 : annualCreditsCost,
        deal_data: {
          order_type: orderType,
          payment_structure: isTrial ? 'upfront' : paymentType,
          camera_model: selectedCamera.name,
          camera_model_id: selectedCamera.id,
          camera_unit_price: cameraPrice,
          plan: selectedPlan.name,
          credits_per_camera: isTrial ? TRIAL_CREDITS : credits,
          price_per_image: pricePerImage,
          camera_cost: cameraCost,
          credits_cost: isTrial ? 0 : annualCreditsCostNet,
          discount_amount: discountAmount,
          discount_type: discountTypes.length > 0 ? discountTypes[0] : 'none',
          discount_types: discountTypes,
          coverage_years: coverageYears,
          commitment_years: (isTrial || isRolling) ? 1 : commitmentYears,
          shots_per_day: shotsPerDay,
          images_per_day: isTrial ? 0 : selectedPlan.imagesPerDay,
          annual_payment_breakdown: paymentType === 'annual' ? {
            year1_payment: total,
            subsequent_annual_payment: annualCreditsCostNet,
            contract_years: coverageYears,
            annual_credits: annualCredits
          } : null,
          accessories: Object.entries(accessoryQuantities)
            .filter(([, qty]) => qty > 0)
            .map(([id, qty]) => {
              const item = accessories.find(a => a.id === id);
              return { id, name: item?.name || id, quantity: qty, unit_price: item?.price || 0 };
            }),
          accessories_cost: accessoriesCost,
          custom_ai_model: customAiModel
        }
      };

      const { error } = await supabase
        .from('crm_deals')
        .update(updateData)
        .eq('id', existingDeal.id);

      if (error) throw error;
      
      toast({ title: "Success", description: "Opportunity updated successfully!" });

      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['partner-deals'] });
      queryClient.invalidateQueries({ queryKey: ['staff-owned-contact-deals'] });
      queryClient.invalidateQueries({ queryKey: ['partner-owned-contact-deals'] });
      queryClient.invalidateQueries({ queryKey: ['organization-deals'] });

      if (onClose) {
        onClose();
      } else {
        setCameraQuantity(1);
        setCredits(1095);
        setContactId("");
        setOrganizationId("");
        setContactSearch("");
        setSelectedContact(null);
        setDiscountTypes([]);
        setPaymentType("annual");
      }
      setOpen(false);
    } catch (error) {
      console.error("❌ [DealCalculator] Error updating opportunity:", error);
      toast({ title: "Error", description: "Failed to update opportunity. Please try again.", variant: "destructive" });
    }
  };

  const handleSaveShortTerm = async () => {
    if (!organizationId) {
      toast({ title: "Validation Error", description: "Organization is required to create an opportunity.", variant: "destructive" });
      return;
    }
    if (!contactId) {
      toast({ title: "Validation Error", description: "Contact person is required to create an opportunity.", variant: "destructive" });
      return;
    }
    setShortTermSaving(true);
    try {
      const q = shortTermQuote(shortTerm.cameras, shortTerm.imagesPerDay, shortTerm.months);
      const pkg = SHORT_TERM_PACKAGES.find(p => p.imagesPerDay === q.imagesPerDay)?.name || 'Core';
      const assignee = currentStaffMember
        ? { assigneeName: `${currentStaffMember.first_name} ${currentStaffMember.last_name}`, assigneeType: 'Staff', assigneeEmail: currentStaffMember.email }
        : { assigneeName: user?.email ? user.email.split('@')[0] : 'System', assigneeType: 'Staff', assigneeEmail: user?.email || 'system@snapshot.live' };
      const title = `${q.cameras}x Camera${q.cameras > 1 ? 's' : ''} Short Term – ${pkg} (${q.termMonths} months)`;
      const short_term = {
        package: pkg, images_per_day: q.imagesPerDay, term_months: q.termMonths, term_weeks: q.termWeeks,
        weekly_per_camera: q.weeklyPerCamera, weekly: q.weekly, quarterly: q.quarterly, annual: q.annual, contract_total: q.contractTotal,
      };
      const stAccessories = Object.entries(accessoryQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => {
          const item = accessories.find(a => a.id === id);
          return { id, name: item?.name || id, quantity: qty, unit_price: item?.price || 0 };
        });
      const grandTotal = q.contractTotal + accessoriesCost;
      const common = {
        title, contact_id: contactId, organization_id: organizationId, value: grandTotal,
        total_cameras: q.cameras, total_images: q.totalImages, total_revenue: grandTotal, image_revenue: q.contractTotal,
      };
      const dealDataBase = {
        term_type: 'short', short_term, order_type: 'cameras_and_credits', payment_structure: 'upfront',
        plan: `Short Term ${pkg}`, images_per_day: q.imagesPerDay, commitment_years: 1, coverage_years: 1,
        camera_cost: 0, credits_cost: q.contractTotal, discount_amount: 0, discount_type: 'none', discount_types: [],
        accessories: stAccessories, accessories_cost: accessoriesCost, custom_ai_model: false,
      };
      if (existingDeal?.id) {
        const { error } = await supabase.from('crm_deals').update({
          ...common, deal_data: { ...(existingDeal.deal_data || {}), ...dealDataBase },
        }).eq('id', existingDeal.id);
        if (error) throw error;
        toast({ title: "Success", description: "Opportunity updated successfully!" });
        ['deals', 'partner-deals', 'staff-owned-contact-deals', 'partner-owned-contact-deals', 'organization-deals']
          .forEach(k => queryClient.invalidateQueries({ queryKey: [k] }));
        onClose?.();
      } else {
        const { data: stage } = await supabase.from('crm_deal_stages').select('id').eq('name', 'Opportunity').maybeSingle();
        await createDeal.mutateAsync({
          ...common, discount_percentage: 0, probability: 50, stage_id: stage?.id || undefined,
          assigned_to_staff_id: currentStaffMember?.id || undefined,
          deal_data: { ...dealDataBase, ...assignee, customerGenerated: false, dealType: 'proposal' },
        } as any);
        setShortTerm({ cameras: 1, imagesPerDay: 4, months: 3 });
        setAccessoryQuantities({});
      }
      setOpen(false);
    } catch (error) {
      console.error("❌ [DealCalculator] Short Term save failed:", error);
      toast({ title: "Error", description: "Failed to save opportunity. Please try again.", variant: "destructive" });
    } finally {
      setShortTermSaving(false);
    }
  };

  const generateQuotationPDF = () => {
    try {
      const contactInfo = existingDeal?.contact || selectedContact;
      
      const doc = new jsPDF({ compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      const generatePDFContent = () => {
        const formatCurrency = (amount: number): string => {
          return amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };
        
        doc.setFontSize(28);
        doc.setTextColor(0, 0, 0);
        const hasOrderReference = existingDeal?.order_reference;
        const documentTitle = hasOrderReference ? 'ORDER SUMMARY' : 'QUOTE';
        doc.text(documentTitle, pageWidth - (hasOrderReference ? 105 : 60), 25);
        
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        let leftY = 40;
        doc.setFont(undefined, 'bold');
        doc.text(COMPANY_NAME, 20, leftY);
        doc.setFont(undefined, 'normal');
        COMPANY_ADDRESS_LINES.forEach((line) => {
          doc.text(line, 20, leftY += 5);
        });
        
        const rightX = pageWidth - 80;
        let rightY = 40;
        
        const currentDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const expiryDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        
        doc.text(`Date: ${currentDate}`, rightX, rightY);
        doc.text(`Expiry: ${expiryDate}`, rightX, rightY += 5);
        const quoteNumber = existingDeal?.quote_number || `QU-${new Date().getFullYear()}-PENDING`;
        doc.text(`Quote Number: ${quoteNumber}`, rightX, rightY += 5);
        doc.text(`Reference: ${existingDeal?.order_reference || ''}`, rightX, rightY += 5);
        
        let customerY = rightY + 10;
        doc.setFont(undefined, 'bold');
        doc.text('Quote Addressed To:', 20, customerY);
        doc.setFont(undefined, 'normal');

        if (contactInfo?.first_name || contactInfo?.last_name) {
          const fullName = `${contactInfo.first_name || ''} ${contactInfo.last_name || ''}`.trim();
          doc.text(fullName, 20, customerY += 5);
        }

        const organizationName = existingDeal?.organization?.name;
        if (organizationName) doc.text(organizationName, 20, customerY += 5);
        if (contactInfo?.email) doc.text(contactInfo.email, 20, customerY += 5);
        
        rightY = customerY;
        
        const summaryY = Math.max(leftY, rightY) + 15;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text('Order Summary', 20, summaryY);
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(`Plan: ${selectedPlan.name} (${coverageYears} year${coverageYears > 1 ? 's' : ''} commitment)`, 20, summaryY + 8);
        doc.text(`Payment: ${paymentType === 'annual' ? 'Annual Contract' : 'Upfront Payment'}`, 20, summaryY + 13);
        if (customAiModel) {
          doc.text(`Custom AI Model: +£0.10/image`, 20, summaryY + 18);
        }
        doc.text(`Per-image cost: £${formatCurrency(pricePerImage)}`, rightX, summaryY + 8);
        doc.text(`Average daily cost/camera: £${formatCurrency(averageCostPerDay)}`, rightX, summaryY + 13);
        
        const contractValue = paymentType === 'annual' 
          ? total + (coverageYears - 1) * annualCreditsCostNet
          : total;

        const tableData = [];
        
        if (orderType === 'cameras_and_credits') {
          const cameraLabelSuffix = isFiveYearFree
            ? ' (FREE - 5yr commitment)'
            : (cameraDiscountAmount > 0 ? ` (${cameraDiscountLabel})` : '');
          const cameraUnitNet = cameraQuantity > 0 ? cameraCostNet / cameraQuantity : 0;
          tableData.push([
            selectedCamera.name + cameraLabelSuffix,
            cameraQuantity.toFixed(2),
            `£${formatCurrency(cameraUnitNet)}`,
            `£${formatCurrency(cameraCostNet)}`
          ]);
          
          let creditDescription = '';
          const imgSuffix = hasImageDiscount ? ` @ £${formatCurrency(discountedPricePerImage)}/img (was £${formatCurrency(pricePerImage)})` : '';
          if (paymentType === 'annual') {
            creditDescription = `${selectedPlan.name} Plan ${annualCredits.toLocaleString()} Images (Year 1)${imgSuffix}`;
          } else {
            creditDescription = `${selectedPlan.name} Plan ${(totalCreditsNeeded * coverageYears).toLocaleString()} Images (${coverageYears} Year${coverageYears > 1 ? 's' : ''})${imgSuffix}`;
          }
          
          const creditUnitPrice = paymentType === 'annual' ? annualCreditsCostNet : (annualCreditsCostNet * coverageYears);
          
          tableData.push([
            creditDescription,
            cameraQuantity.toFixed(2),
            `£${formatCurrency(creditUnitPrice / cameraQuantity)}`,
            `£${formatCurrency(creditUnitPrice)}`
          ]);
        } else {
          let creditDescription = '';
          const imgSuffix = hasImageDiscount ? ` @ £${formatCurrency(discountedPricePerImage)}/img (was £${formatCurrency(pricePerImage)})` : '';
          if (paymentType === 'annual') {
            creditDescription = `${selectedPlan.name} Plan ${annualCredits.toLocaleString()} Images (Annual)${imgSuffix}`;
          } else {
            creditDescription = `${selectedPlan.name} Plan ${(totalCreditsNeeded * coverageYears).toLocaleString()} Images (${coverageYears} Year${coverageYears > 1 ? 's' : ''})${imgSuffix}`;
          }
          const creditTotal = paymentType === 'annual' ? annualCreditsCostNet : (annualCreditsCostNet * coverageYears);
          tableData.push([
            creditDescription,
            '1.00',
            `£${formatCurrency(creditTotal)}`,
            `£${formatCurrency(creditTotal)}`
          ]);
        }

        // Add accessory line items
        Object.entries(accessoryQuantities).filter(([, qty]) => qty > 0).forEach(([id, qty]) => {
          const item = accessories.find(a => a.id === id);
          if (item) {
            const itemTotal = qty * item.price;
            const discounted = accessoriesDiscountAmount > 0;
            tableData.push([
              discounted ? `${item.name} (${accessoriesDiscountLabel})` : item.name,
              qty.toFixed(2),
              discounted ? `£0.00` : `£${formatCurrency(item.price)}`,
              discounted ? `£0.00` : `£${formatCurrency(itemTotal)}`
            ]);
          }
        });

        // Discounts are now applied inline on the camera/credit/accessory lines above; only show net subtotal.
        const calculatedSubtotal = paymentType === 'annual'
          ? (cameraCostNet + annualCreditsCostNet + accessoriesCostNet)
          : (annualCreditsCostNet * coverageYears + cameraCostNet + accessoriesCostNet);
        tableData.push(['', '', 'Subtotal:', `£${formatCurrency(calculatedSubtotal)}`]);
        
        if (paymentType === 'annual') {
          tableData.push(['', '', 'Year 1 Total:', `£${formatCurrency(total)}`]);
          tableData.push(['', '', 'Annual Payment:', `£${formatCurrency(annualCreditsCostNet)}`]);
          tableData.push(['', '', 'Contract Value:', `£${formatCurrency(contractValue)}`]);
        } else {
          tableData.push(['', '', 'Total:', `£${formatCurrency(total)}`]);
        }
        
        (doc as any).autoTable({
          startY: summaryY + 20,
          head: [['Description', 'Qty', 'Unit Price', 'Total']],
          body: tableData,
          styles: { fontSize: 8, cellPadding: 3, lineColor: [255, 255, 255], lineWidth: 0, halign: 'left' },
          headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', lineColor: [255, 255, 255], lineWidth: 0, halign: 'left' },
          bodyStyles: { lineColor: [255, 255, 255], lineWidth: 0, halign: 'left' },
          columnStyles: {
            0: { cellWidth: 90, halign: 'left' },
            1: { cellWidth: 25, halign: 'left' },
            2: { cellWidth: 35, halign: 'left' },
            3: { cellWidth: 35, halign: 'left' }
          },
          didParseCell: function(data: any) {
            const rowIndex = data.row.index;
            const accessoryRowCount = Object.values(accessoryQuantities).filter((q) => (q as number) > 0).length;
            const lineItemRowCount = (orderType === 'cameras_and_credits' ? 2 : 1) + accessoryRowCount;
            const isFinancialRow = rowIndex >= lineItemRowCount;
            const isEmptyRow = data.cell.text[0] === '' && data.column.index === 0 && rowIndex >= lineItemRowCount;
            const isTotalRow = data.cell.text[0] && (data.cell.text[0].includes('Total:') || data.cell.text[0].includes('Contract Value:'));
            
            if (isEmptyRow) {
              data.cell.styles.lineWidth = 0;
              data.cell.styles.fillColor = [255, 255, 255];
            } else if (isFinancialRow && !isEmptyRow) {
              if (data.column.index >= 2) {
                data.cell.styles.fontStyle = isTotalRow ? 'bold' : 'normal';
                data.cell.styles.fontSize = isTotalRow ? 9 : 8;
                data.cell.styles.lineWidth = 0;
                if (data.cell.text[0] && data.cell.text[0].includes('Discount')) {
                  data.cell.styles.textColor = [200, 0, 0];
                }
              } else {
                data.cell.styles.lineWidth = 0;
                data.cell.text = [''];
              }
            } else {
              data.cell.styles.lineWidth = 0;
            }
          },
          margin: { left: 20, right: 20 },
          tableLineColor: [255, 255, 255],
          tableLineWidth: 0,
          theme: 'plain'
        });
        
        let currentY = (doc as any).lastAutoTable.finalY + 5;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('All amounts are ex VAT', 20, currentY);
        currentY += 8;
        
        // Agreement Duration highlight box
        const durationBoxHeight = 14;
        doc.setFillColor(223, 212, 201); // brand beige #DFD4C9
        doc.rect(20, currentY, pageWidth - 40, durationBoxHeight, 'F');
        doc.setTextColor(50, 21, 33); // brand plum #321521
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Agreement Duration', 24, currentY + 5);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(
          isRolling
            ? 'Agreement duration: 1 year, rolling, commences on delivery.'
            : `Agreement duration: ${coverageYears} year${coverageYears > 1 ? 's' : ''}, commences on delivery.`,
          24,
          currentY + 11
        );
        currentY += durationBoxHeight + 6;
        doc.setTextColor(100, 100, 100);

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Basic Terms', 20, currentY);
        currentY += 8;
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        
        const terms = [
          `Commitment Period – Refers to the contracted service duration of ${coverageYears} year${coverageYears > 1 ? 's' : ''} during which camera connectivity is maintained and images are delivered.`,
          '',
          'Additional Credits – May be purchased at any time at the per-image rate of the originally selected package.',
          '',
          'Included Features – Image credit pricing includes privacy filtering, AI-driven image search*, and optional real-time notifications.',
          '',
          paymentType === 'annual' && coverageYears > 1
            ? `Payment Structure – Annual Contract: Year 1 payment of £${formatCurrency(total)} includes camera${effectiveCameraQuantity > 1 ? 's' : ''} and first year credits. Subsequent years: £${formatCurrency(annualCreditsCostNet)} annually for ${coverageYears - 1} year${coverageYears - 1 > 1 ? 's' : ''}.`
            : paymentType === 'annual'
              ? `Payment Structure – Payment of £${formatCurrency(total)} includes camera${effectiveCameraQuantity > 1 ? 's' : ''} and the first year credits.`
              : 'Payment Options – Full Upfront Payment: Provides full access to all purchased credits for the entire coverage period.',
          '',
          isFiveYearFree ? 'Camera Included – 5-year commitment includes camera hardware at no additional cost.' : '',
          isFiveYearFree ? '' : '',
          'Image Retention – Images are stored on the Snapshot Live platform for 90 days. Customers may extend storage by ingesting images into their own system via API**.',
          '',
          '*see full terms and conditions for details',
          '**API integration cost POA'
        ];
        
        terms.forEach(term => {
          if (term === '') {
            currentY += 3;
          } else {
            const lines = doc.splitTextToSize(term, pageWidth - 40);
            doc.text(lines, 20, currentY);
            currentY += lines.length * 3.5 + 1;
          }
        });
        
        doc.setFontSize(8);
        doc.text(doc.splitTextToSize(COMPANY_PDF_FOOTER, pageWidth - 40), 20, doc.internal.pageSize.getHeight() - 13);
        
        const filePrefix = hasOrderReference ? 'Order_Summary' : 'Quote';
        const quoteRef = existingDeal?.quote_number || `TEMP-${Date.now().toString().slice(-4)}`;
        const fileName = `${filePrefix}_${quoteRef}_${selectedContact ? `${selectedContact.first_name}_${selectedContact.last_name}` : 'Opportunity'}.pdf`;
        doc.save(fileName);
        
        toast({
          title: `${hasOrderReference ? 'Order Summary' : 'Quote'} Downloaded`,
          description: `Professional ${hasOrderReference ? 'order summary' : 'quotation'} saved as ${fileName}`,
        });
      };
      
      const logoImg = document.createElement('img');
      logoImg.onload = function() {
        try {
          // Downscale logo to keep PDF small (original is 4321x1239)
          const targetW = 480;
          const scale = targetW / logoImg.width;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = targetW;
          canvas.height = Math.round(logoImg.height * scale);
          ctx!.fillStyle = '#ffffff';
          ctx!.fillRect(0, 0, canvas.width, canvas.height);
          ctx!.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
          const logoDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          const logoWidth = 60;
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          doc.addImage(logoDataUrl, 'JPEG', 20, 15, logoWidth, logoHeight, undefined, 'FAST');
          generatePDFContent();
        } catch (error) {
          doc.setFontSize(24);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(0, 100, 150);
          doc.text('SNAPSHOT LIVE', 20, 25);
          generatePDFContent();
        }
      };
      
      logoImg.onerror = function() {
        doc.setFontSize(24);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 100, 150);  
        doc.text('SNAPSHOT LIVE', 20, 25);
        generatePDFContent();
      };
      
      logoImg.src = '/snapshot-logo.png';
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({ title: "Error", description: "Failed to generate PDF. Please try again.", variant: "destructive" });
    }
  };

  const accessoriesCard = (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="h-5 w-5" />
                  Accessories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {accessories.map((accessory) => {
                    const qty = accessoryQuantities[accessory.id] || 0;
                    return (
                      <div key={accessory.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                        <div>
                          <div className="font-medium text-slate-800">{accessory.name}</div>
                          <div className="text-sm text-slate-600">{accessory.description}</div>
                          <div className="text-sm font-semibold text-teal-600">£{accessory.price} ex VAT</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 border-slate-300"
                            onClick={() => setAccessoryQuantities(prev => ({
                              ...prev,
                              [accessory.id]: Math.max(0, (prev[accessory.id] || 0) - 1)
                            }))}
                            disabled={qty <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-lg font-bold w-8 text-center">{qty}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 border-slate-300"
                            onClick={() => setAccessoryQuantities(prev => ({
                              ...prev,
                              [accessory.id]: (prev[accessory.id] || 0) + 1
                            }))}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
  );

  const dealAssignmentCard = (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-5 w-5" />
                  Deal Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {currentStaffMember && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700 font-medium">Assigned to:</span>
                      <Badge className="bg-blue-600 text-white text-sm">
                        {currentStaffMember.first_name} {currentStaffMember.last_name}
                      </Badge>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">{currentStaffMember.email}</div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">
                      Contact <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="Search contacts by name or email..."
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        className="pr-10 border-slate-300 focus:border-teal-500"
                      />
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                    
                      {contactSearch && filteredContacts.length > 0 && (
                        <div className="border border-slate-300 rounded-lg bg-white max-h-40 overflow-y-auto">
                          {filteredContacts.slice(0, 5).map((contact) => (
                            <button
                              key={contact.id}
                              onClick={() => handleContactSelect(contact.id)}
                              className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-200 last:border-b-0"
                            >
                              <div className="font-medium text-slate-800">
                                {contact.first_name} {contact.last_name}
                              </div>
                              <div className="text-sm text-slate-600">{contact.email}</div>
                              {contact.organizations && (
                                <div className="text-xs text-slate-500">{contact.organizations.name}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {selectedContact && (
                        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mt-2">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-teal-600" />
                            <span className="font-medium text-slate-800">
                              {selectedContact.first_name} {selectedContact.last_name}
                            </span>
                          </div>
                          <div className="text-sm text-slate-600">{selectedContact.email}</div>
                          {selectedContact.organizations && (
                            <div className="flex items-center gap-2 mt-2">
                              <Building className="h-4 w-4 text-teal-600" />
                              <span className="text-sm text-slate-700">{selectedContact.organizations.name}</span>
                            </div>
                          )}
                          {selectedContact.staff_owner && (
                            <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2">
                              <div className="text-xs text-blue-700 font-medium">Contact Owner:</div>
                              <div className="text-sm text-blue-800">
                                {selectedContact.staff_owner.first_name} {selectedContact.staff_owner.last_name}
                              </div>
                            </div>
                          )}
                          {selectedContact.partner_owner && (
                            <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
                              <div className="text-xs text-yellow-700 font-medium">Partner Assigned:</div>
                              <div className="text-sm text-yellow-800">
                                {selectedContact.partner_owner.first_name} {selectedContact.partner_owner.last_name}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Organization *</Label>
                    <Select value={organizationId} onValueChange={setOrganizationId} required>
                      <SelectTrigger className="border-slate-300 focus:border-teal-500">
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent className="bg-white z-50">
                        {sortOrganisations(companies, (company) => company.name).map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {!existingDeal && (
        <DialogTrigger asChild>
          <Button 
            className="text-slate-800 font-semibold rounded-lg shadow-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#DFEF97" }}
          >
            <Calculator className="h-4 w-4 mr-2" />
            Create Opportunity
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-slate-50 border-slate-200">
        <DialogHeader className="bg-component-background -mx-6 -mt-6 px-6 py-4 rounded-t-xl border-b border-foreground text-foreground">
          <DialogTitle className="text-xl font-semibold text-foreground">
            Opportunity Calculator
          </DialogTitle>
          <p className="text-sm text-foreground">
            Create a new opportunity with cameras and credits for your customer.
          </p>
        </DialogHeader>

        <div className="mt-6 flex items-center gap-3">
          <span className="text-sm font-medium text-foreground">Term</span>
          <Tabs value={termType} onValueChange={(v) => setTermType(v as 'long' | 'short')}>
            <TabsList className="grid grid-cols-2 w-64">
              <TabsTrigger value="long">Long Term</TabsTrigger>
              <TabsTrigger value="short">Short Term</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {termType === 'short' ? (
          <div className="mt-6 space-y-6">
            {dealAssignmentCard}
            <ShortTermCalculator
              accessoriesCard={accessoriesCard}
              accessoriesCost={accessoriesCost}
              onDownloadPdf={async () => {
                try {
                  const fileName = await downloadShortTermQuotePdf({
                    ...shortTerm,
                    accessories: Object.entries(accessoryQuantities).filter(([, q]) => q > 0).map(([id, q]) => {
                      const a = accessories.find(x => x.id === id);
                      return { name: a?.name || id, quantity: q, unit_price: a?.price || 0 };
                    }),
                    quoteNumber: existingDeal?.quote_number,
                    contact: existingDeal?.contact || selectedContact,
                    organizationName: existingDeal?.organization?.name || companies.find((c: any) => c.id === organizationId)?.name,
                  });
                  toast({ title: "Quote Downloaded", description: `Saved as ${fileName}` });
                } catch (e) {
                  console.error(e);
                  toast({ title: "Error", description: "Failed to generate PDF. Please try again.", variant: "destructive" });
                }
              }}
              cameras={shortTerm.cameras}
              imagesPerDay={shortTerm.imagesPerDay}
              months={shortTerm.months}
              onChange={setShortTerm}
              onSubmit={handleSaveShortTerm}
              submitting={shortTermSaving || createDeal.isPending}
              isEdit={!!existingDeal}
            />
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Left Column - Calculator */}
          <div className="lg:col-span-2 space-y-6">
            {dealAssignmentCard}

            {/* Order Type Selection */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShoppingCart className="h-5 w-5" />
                  Order Type
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Tabs value={orderType} onValueChange={(value) => setOrderType(value as 'cameras_and_credits' | 'credits_only')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="cameras_and_credits" className="flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      Cameras + Credits
                    </TabsTrigger>
                    <TabsTrigger value="credits_only" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Credits Only
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="cameras_and_credits" className="mt-4">
                    <p className="text-sm text-slate-600">
                      New cameras with credit packages for a complete solution.
                    </p>
                  </TabsContent>
                  <TabsContent value="credits_only" className="mt-4">
                    <p className="text-sm text-slate-600">
                      Add credits to existing cameras or devices.
                    </p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Camera Model Selection - Only show for cameras_and_credits */}
            {orderType === 'cameras_and_credits' && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="h-5 w-5" />
                    Camera Model
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {visibleCameraVariants.map((camera) => (
                      <div
                        key={camera.id}
                        onClick={() => {
                          setSelectedCamera(camera);
                          toast({ title: `${camera.name} Selected!`, description: camera.description });
                        }}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedCamera.id === camera.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{camera.name}</h4>
                            <p className="text-sm text-slate-600 mt-1">{camera.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-teal-600">£{camera.price}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Camera Quantity - Only show for cameras_and_credits */}
            {orderType === 'cameras_and_credits' && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="h-5 w-5" />
                    Camera Quantity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={cameraQuantity <= 1}
                        className="border-slate-300"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        min={1}
                        value={cameraQuantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 1;
                          setCameraQuantity(Math.max(1, value));
                        }}
                        onBlur={(e) => {
                          const value = parseInt(e.target.value);
                          if (isNaN(value) || value < 1) setCameraQuantity(1);
                        }}
                        className="text-3xl font-bold text-slate-800 w-24 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(1)}
                        className="border-slate-300"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {isFleetMode && (
                      <Badge className="bg-yellow-500 text-slate-800 font-semibold">
                        Fleet Mode
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-2">
                    Total credits needed: {totalCreditsNeeded.toLocaleString()} per year
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Plan Tiers */}
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="text-lg">Choose Your Plan</CardTitle>
                  {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="show-legacy-toggle" className="text-xs text-foreground/80 cursor-pointer">
                      Show legacy packages
                    </Label>
                    <Switch
                      id="show-legacy-toggle"
                      checked={showLegacy}
                      onCheckedChange={setShowLegacy}
                    />
                  </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...planTiers, ...(showLegacy && isSuperuser ? legacyPlanTiers : [])].map((plan) => {
                    const isLegacy = legacyPlanTiers.some(l => l.name === plan.name);
                    return (
                    <button
                      key={plan.name}
                      onClick={() => handlePlanSelect(plan)}
                      style={{ backgroundColor: isLegacy ? "#F1F5F9" : plan.name === "Trial" ? "#E8F5E9" : plan.name === "Core" ? "#DFD4C9" : plan.name === "Pro" ? "#321521" : plan.name === "Plus" ? "#DFEF97" : "#F0EDE6" }}
                      className={`p-4 rounded-lg border-2 text-left transition-all hover:shadow-md ${
                        selectedPlan.name === plan.name 
                          ? 'border-teal-500 shadow-md' 
                          : 'hover:border-teal-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className={"font-semibold " + (plan.name === "Pro" ? "text-white" : "text-slate-800")}>{plan.name}</h3>
                          {isLegacy && (
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-slate-600 border-slate-400">
                              Legacy
                            </Badge>
                          )}
                        </div>
                        {plan.name !== 'Trial' && (
                          <Badge variant="outline" className={`text-xs ${plan.name === 'Pro' ? 'text-white border-white' : 'text-black border-black'}`}>
                            {plan.imagesPerDay} img/day
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm ${plan.name === 'Pro' ? 'text-white' : 'text-slate-600'} mb-2`}>{plan.description}</p>
                      <div className="flex justify-between items-center">
                        <span className={`text-lg font-bold ${plan.name === 'Pro' ? 'text-white' : 'text-black'}`}>
                          {plan.name === 'Trial' ? '£495 camera' : `£${plan.pricePerImage}/image`}
                        </span>
                        <span className={`text-xs ${plan.name === 'Pro' ? 'text-white' : 'text-black'}`}>
                          {plan.name === 'Trial' ? '600 credits included' : `${plan.imagesPerYear.toLocaleString()}/year`}
                        </span>
                      </div>
                      {selectedPlan.name === plan.name && (
                        <div className="mt-2">
                          <Badge className="bg-teal-primary text-black text-xs">Selected</Badge>
                        </div>
                      )}
                    </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Custom AI Model - Hidden for Trial */}
            {!isTrial && (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BrainCircuit className="h-5 w-5" />
                  Custom AI Model
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-slate-700 font-medium">Enable Custom AI Model</Label>
                    <p className="text-sm text-slate-600">
                      Adds £0.10/image for enhanced AI image analysis
                    </p>
                    {customAiModel && (
                      <p className="text-sm font-semibold text-teal-700">
                        Effective per-image price: £{(selectedPlan.pricePerImage + 0.10).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={customAiModel}
                    onCheckedChange={setCustomAiModel}
                  />
                </div>
              </CardContent>
            </Card>
            )}

            {/* Credit Slider - Hidden for Trial */}
            {!isTrial && (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Image className="h-5 w-5" />
                  Images per Camera (per year)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-slate-700 font-medium">Images: {credits.toLocaleString()}</Label>
                    <div className="text-sm text-slate-600">
                      ~{shotsPerDay} images/day
                    </div>
                  </div>
                  <Slider
                    value={[credits]}
                    onValueChange={handleCreditsChange}
                    min={selectedPlan.imagesPerYear}
                    max={20000}
                    step={20}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{selectedPlan.imagesPerYear.toLocaleString()} (included)</span>
                    <span>20,000</span>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">Included with {selectedPlan.name}:</span>
                      <span className="font-semibold text-slate-800">{selectedPlan.imagesPerYear.toLocaleString()} images/year</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Commitment Period - Hidden for Trial and 1 Year Rolling */}
            {!isTrial && !isRolling && (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <div className="flex items-center justify-between gap-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Lock className="h-5 w-5" />
                    Commitment Period
                  </CardTitle>
                  {isSuperuser && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="show-two-year-toggle" className="text-xs text-foreground/80 cursor-pointer">
                      Show 2-year option
                    </Label>
                    <Switch
                      id="show-two-year-toggle"
                      checked={showTwoYear}
                      onCheckedChange={(on) => {
                        setShowTwoYear(on);
                        if (!on && commitmentYears === 2) setCommitmentYears(3);
                      }}
                    />
                  </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className={`grid gap-4 ${showTwoYear && isSuperuser ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'}`}>
                  {showTwoYear && isSuperuser && (
                  <button
                    onClick={() => setCommitmentYears(2)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      commitmentYears === 2
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-slate-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-800 text-lg">2 Years</div>
                    <p className="text-sm text-slate-600 mt-1">Short commitment</p>
                    {commitmentYears === 2 && (
                      <Badge className="bg-teal-primary text-black text-xs mt-2">Selected</Badge>
                    )}
                  </button>
                  )}
                  <button
                    onClick={() => setCommitmentYears(3)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      commitmentYears === 3
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-slate-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-800 text-lg">3 Years</div>
                    <p className="text-sm text-slate-600 mt-1">Standard commitment</p>
                    {commitmentYears === 3 && (
                      <Badge className="bg-teal-primary text-black text-xs mt-2">Selected</Badge>
                    )}
                  </button>
                  <button
                    onClick={() => setCommitmentYears(5)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      commitmentYears === 5
                        ? 'border-teal-500 bg-teal-50 shadow-md'
                        : 'border-slate-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-800 text-lg">5 Years</div>
                    <p className="text-sm text-slate-600 mt-1">Best value</p>
                    <div className="flex items-center gap-1 mt-2">
                      <Gift className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-600">Camera included FREE!</span>
                    </div>
                    {commitmentYears === 5 && (
                      <Badge className="bg-teal-primary text-black text-xs mt-2">Selected</Badge>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
            )}

            {accessoriesCard}

            {!isTrial && (
            <Card className="border-slate-200">
              <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CreditCard className="h-5 w-5" />
                  Payment Structure
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Tabs value={paymentType} onValueChange={(value) => setPaymentType(value as 'annual' | 'upfront')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="annual" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Annual Contract
                    </TabsTrigger>
                    <TabsTrigger value="upfront" className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Upfront Payment
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="annual" className="mt-4">
                    <p className="text-sm text-slate-600">
                      Spread payments over {coverageYears} years with annual commitments. Lower Year 1 payment includes cameras + first year credits.
                    </p>
                  </TabsContent>
                  <TabsContent value="upfront" className="mt-4">
                    <p className="text-sm text-slate-600">
                      Single payment for full {coverageYears}-year coverage. All credits allocated immediately.
                    </p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            )}

            {/* Discount Field - Only for Superusers and Partners */}
            {canEditDiscount && (
              <Card className="border-slate-200">
                <CardHeader className="bg-component-background text-foreground border-b border-foreground rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    Discount
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Discount Options</Label>
                    <div className="space-y-3">
                      {[
                        { id: 'camera_10', label: '£10 off per Camera', group: 'camera', trialOnly: false },
                        { id: 'camera_50', label: '50% off Camera cost', group: 'camera', trialOnly: false },
                        { id: 'camera_100', label: '100% off Camera cost', group: 'camera', trialOnly: false },
                        { id: 'image_2p', label: '£0.02 off per Image', group: 'image', trialOnly: false },
                        { id: 'image_4p', label: '£0.04 off per Image', group: 'image', trialOnly: false },
                        { id: 'image_10p', label: '£0.10 off per Image', group: 'image', trialOnly: false },
                        { id: 'image_14p', label: '£0.14 off per Image', group: 'image', trialOnly: false },
                        { id: 'essential_100', label: '100% off Pilot Pack', group: 'camera', trialOnly: true },
                        { id: 'accessories_100', label: '100% off Accessories', group: 'accessories', trialOnly: false, alwaysShow: true },
                      ].filter(option => option.alwaysShow || (isTrial ? option.trialOnly : !option.trialOnly)).map(option => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`discount-${option.id}`}
                            checked={discountTypes.includes(option.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                if (option.group === 'camera') {
                                  // Camera discounts are mutually exclusive
                                  setDiscountTypes(prev => [
                                    ...prev.filter(d => !['camera_10', 'camera_50', 'camera_100', 'essential_100'].includes(d)),
                                    option.id
                                  ]);
                                } else if (option.group === 'image') {
                                  // Image discounts are mutually exclusive
                                  setDiscountTypes(prev => [
                                    ...prev.filter(d => !['image_2p', 'image_4p', 'image_10p', 'image_14p'].includes(d)),
                                    option.id
                                  ]);
                                } else {
                                  setDiscountTypes(prev => [...prev, option.id]);
                                }
                              } else {
                                setDiscountTypes(prev => prev.filter(d => d !== option.id));
                              }
                            }}
                          />
                          <label htmlFor={`discount-${option.id}`} className="text-sm text-slate-700 cursor-pointer">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-slate-600">
                      Special discount options for this opportunity.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <Card className="border-slate-200 sticky top-4">
              <CardHeader className="bg-lime-button text-black rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5" />
                  Opportunity Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="space-y-3">
                  {isTrial ? (
                    <>
                      {/* Trial Summary */}
                      <div className="bg-accent border border-border rounded-lg p-3 mb-4">
                        <div className="text-sm font-semibold mb-2">Trial Plan</div>
                        <div className="text-xs text-muted-foreground">£495 camera (continue or return) + 600 credits included</div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>{cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}</span>
                          <span>£{fmtGBP(cameraCost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>{TRIAL_CREDITS} Credits included</span>
                          <span>Included</span>
                        </div>
                        {Object.entries(accessoryQuantities).filter(([, qty]) => qty > 0).map(([id, qty]) => {
                          const item = accessories.find(a => a.id === id);
                          if (!item) return null;
                          const itemTotal = qty * item.price;
                          const discounted = accessoriesDiscountAmount > 0;
                          return (
                            <div key={id} className="flex justify-between text-sm">
                              <span className="text-slate-600">
                                {qty}x {item.name}
                                {discounted && <span className="ml-1 text-xs text-emerald-700">({accessoriesDiscountLabel} applied)</span>}
                              </span>
                              <span>
                                {discounted ? (
                                  <>
                                    <span className="line-through text-slate-400 mr-2">£{fmtGBP(itemTotal)}</span>
                                    £{fmtGBP(0)}
                                  </>
                                ) : (
                                  <>£{fmtGBP(itemTotal)}</>
                                )}
                              </span>
                            </div>
                          );
                        })}
                        <div className="border-t pt-3">
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total</span>
                            <span>£{fmtGBP(total)}</span>
                          </div>
                          <div className="text-xs text-slate-500 text-center mt-2">All amounts are ex VAT</div>
                        </div>
                      </div>
                    </>
                  ) : paymentType === 'annual' ? (
                    <>
                      {/* Annual Contract Breakdown */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="text-sm font-semibold text-blue-800 mb-2">Annual Contract - {coverageYears} Year Commitment</div>
                        <div className="text-xs text-blue-600">
                          {selectedPlan.name} Plan • {selectedPlan.imagesPerDay} images/day
                          {isFiveYearFree && ' • Camera FREE'}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="font-semibold text-slate-800 border-b border-slate-200 pb-2">Year 1 Payment</div>
                        
                        {effectiveCameraQuantity > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              {cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}
                              {isFiveYearFree && ' (FREE)'}
                              {cameraDiscountAmount > 0 && !isFiveYearFree && (
                                <span className="ml-1 text-xs text-emerald-700">({cameraDiscountLabel} applied)</span>
                              )}
                            </span>
                            <span className="font-semibold text-slate-800">
                              {cameraDiscountAmount > 0 && !isFiveYearFree ? (
                                <>
                                  <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(cameraCost)}</span>
                                  £{fmtGBP(cameraCostNet)}
                                </>
                              ) : (
                                <>£{fmtGBP(cameraCost)}</>
                              )}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">
                            Year 1 Credits ({annualCredits.toLocaleString()})
                            {hasImageDiscount && (
                              <span className="ml-1 text-xs text-emerald-700">@ £{fmtGBP(discountedPricePerImage)}/img (was £{fmtGBP(pricePerImage)})</span>
                            )}
                          </span>
                          <span className="font-semibold text-slate-800">
                            {hasImageDiscount ? (
                              <>
                                <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(annualCreditsCost)}</span>
                                £{fmtGBP(annualCreditsCostNet)}
                              </>
                            ) : (
                              <>£{fmtGBP(annualCreditsCost)}</>
                            )}
                          </span>
                        </div>

                        {Object.entries(accessoryQuantities).filter(([, qty]) => qty > 0).map(([id, qty]) => {
                          const item = accessories.find(a => a.id === id);
                          if (!item) return null;
                          const itemTotal = qty * item.price;
                          const discounted = accessoriesDiscountAmount > 0;
                          return (
                            <div key={id} className="flex justify-between text-sm">
                              <span className="text-slate-600">
                                {qty}x {item.name}
                                {discounted && <span className="ml-1 text-xs text-emerald-700">({accessoriesDiscountLabel} applied)</span>}
                              </span>
                              <span className="font-semibold text-slate-800">
                                {discounted ? (
                                  <>
                                    <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(itemTotal)}</span>
                                    £{fmtGBP(0)}
                                  </>
                                ) : (
                                  <>£{fmtGBP(itemTotal)}</>
                                )}
                              </span>
                            </div>
                          );
                        })}

                        <div className="border-t border-slate-200 pt-2">
                          <div className="flex justify-between font-bold">
                            <span className="text-slate-800">Year 1 Total</span>
                            <span className="text-slate-800">£{fmtGBP(total)}</span>
                          </div>
                          <div className="text-xs text-slate-500 text-center mt-2">All amounts are ex VAT</div>
                        </div>
                      </div>

                      {coverageYears > 1 && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                          <div className="text-sm font-semibold text-slate-800 mb-2">Subsequent Years ({coverageYears - 1} years)</div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              Annual Credits ({annualCredits.toLocaleString()})
                              {hasImageDiscount && (
                                <span className="ml-1 text-xs text-emerald-700">@ £{fmtGBP(discountedPricePerImage)}/img</span>
                              )}
                            </span>
                            <span className="font-semibold text-slate-800">£{fmtGBP(annualCreditsCostNet)}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t border-slate-300 pt-2">
                            <span className="text-slate-800">Annual Payment</span>
                            <span className="text-slate-800">£{fmtGBP(annualCreditsCostNet)}</span>
                          </div>
                          <div className="text-xs text-slate-500 text-center mt-2">All amounts are ex VAT</div>
                        </div>
                      )}

                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                        <div className="text-sm font-semibold text-teal-800">Total Contract Value</div>
                        <div className="text-lg font-bold text-teal-800">
                          £{fmtGBP(total + (coverageYears - 1) * annualCreditsCostNet)}
                        </div>
                        <div className="text-xs text-teal-700 mt-1">All amounts are ex VAT</div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Upfront Payment Breakdown */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="text-sm font-semibold text-green-800 mb-2">Upfront Payment - {coverageYears} Year Coverage</div>
                        <div className="text-xs text-green-600">
                          All credits allocated immediately
                          {isFiveYearFree && ' • Camera FREE'}
                        </div>
                      </div>

                      {effectiveCameraQuantity > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">
                            {cameraQuantity}x Camera{cameraQuantity > 1 ? 's' : ''}
                            {isFiveYearFree && ' (FREE)'}
                            {cameraDiscountAmount > 0 && !isFiveYearFree && (
                              <span className="ml-1 text-xs text-emerald-700">({cameraDiscountLabel} applied)</span>
                            )}
                          </span>
                          <span className="font-semibold text-slate-800">
                            {cameraDiscountAmount > 0 && !isFiveYearFree ? (
                              <>
                                <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(cameraCost)}</span>
                                £{fmtGBP(cameraCostNet)}
                              </>
                            ) : (
                              <>£{fmtGBP(cameraCost)}</>
                            )}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <span className="text-slate-600">
                          {(totalCreditsNeeded * coverageYears).toLocaleString()} Credits ({coverageYears} year{coverageYears > 1 ? 's' : ''})
                          {hasImageDiscount && (
                            <span className="ml-1 text-xs text-emerald-700">@ £{fmtGBP(discountedPricePerImage)}/img (was £{fmtGBP(pricePerImage)})</span>
                          )}
                        </span>
                        <span className="font-semibold text-slate-800">
                          {hasImageDiscount ? (
                            <>
                              <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(annualCreditsCost * coverageYears)}</span>
                              £{fmtGBP(annualCreditsCostNet * coverageYears)}
                            </>
                          ) : (
                            <>£{fmtGBP(annualCreditsCost * coverageYears)}</>
                          )}
                        </span>
                      </div>

                      {Object.entries(accessoryQuantities).filter(([, qty]) => qty > 0).map(([id, qty]) => {
                        const item = accessories.find(a => a.id === id);
                        if (!item) return null;
                        const itemTotal = qty * item.price;
                        const discounted = accessoriesDiscountAmount > 0;
                        return (
                          <div key={id} className="flex justify-between">
                            <span className="text-slate-600">
                              {qty}x {item.name}
                              {discounted && <span className="ml-1 text-xs text-emerald-700">({accessoriesDiscountLabel} applied)</span>}
                            </span>
                            <span className="font-semibold text-slate-800">
                              {discounted ? (
                                <>
                                  <span className="line-through text-slate-400 mr-2 font-normal">£{fmtGBP(itemTotal)}</span>
                                  £{fmtGBP(0)}
                                </>
                              ) : (
                                <>£{fmtGBP(itemTotal)}</>
                              )}
                            </span>
                          </div>
                        );
                      })}

                      <div className="border-t border-slate-200 pt-3">
                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-slate-800">Total Upfront Payment</span>
                          <span className="text-slate-800">£{fmtGBP(total)}</span>
                        </div>
                        <div className="text-xs text-slate-500 text-center mt-2">All amounts are ex VAT</div>
                      </div>
                    </>
                  )}

                  <div className="bg-component-background border rounded-lg p-3" style={{ borderColor: "#FB4F2B" }}>
                    <div className="text-sm space-y-1">
                      {customAiModel && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Custom AI Model:</span>
                          <span className="font-semibold text-teal-700">+£0.10/image</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Per-image cost:</span>
                        <span className="font-semibold text-slate-800">£{pricePerImage.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Avg. cost/day/camera:</span>
                        <span className="font-semibold text-slate-800">£{fmtGBP(averageCostPerDay)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Avg. cost/week/camera:</span>
                        <span className="font-semibold text-slate-800">£{fmtGBP(averageCostPerWeek)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Avg. cost/month/camera:</span>
                        <span className="font-semibold text-slate-800">£{fmtGBP(averageCostPerMonth)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Plan:</span>
                        <span className="font-semibold text-teal-700">{selectedPlan.name}</span>
                      </div>
                      {!isTrial && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Commitment:</span>
                          <span className="font-semibold text-teal-700">
                            {isRolling ? '1 year (rolling)' : `${coverageYears} years`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={async () => { await generateQuotationPDF(); }}
                    variant="outline"
                    className="w-full border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Quotation PDF
                  </Button>

                  <Button
                    onClick={existingDeal ? handleUpdateDeal : handleCreateDeal}
                    disabled={createDeal.isPending}
                    className="w-full bg-[#DFEF97] hover:bg-[#DFEF97]/90 text-black font-semibold rounded-lg shadow-md"
                  >
                    {createDeal.isPending ? 
                      (existingDeal ? "Updating Opportunity..." : "Creating Opportunity...") : 
                      (existingDeal ? "Update Opportunity" : "Create Opportunity")
                    }
                  </Button>

                  {/* MSA Direct Conversion */}
                  {existingDeal && hasMsa && (
                    <div className="space-y-2 pt-2 border-t border-slate-200">
                      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg">
                            <FileCheck className="mr-2 h-4 w-4" />
                            Convert to Order (MSA)
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle>Convert Deal to Order</DialogTitle>
                            <DialogDescription>
                              This organization has a Master Service Agreement. Enter the customer's PO number to convert this deal directly to an order.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="customer-po">Customer PO Number *</Label>
                              <Input
                                id="customer-po"
                                value={customerPO}
                                onChange={(e) => setCustomerPO(e.target.value)}
                                placeholder="e.g. PO-2025-00123"
                              />
                            </div>
                            {msaReference && (
                              <p className="text-sm text-muted-foreground">
                                MSA Reference: <span className="font-medium">{msaReference}</span>
                              </p>
                            )}
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleDirectConversion}
                              disabled={!customerPO.trim() || isConverting}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {isConverting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                              Convert to Order
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      
                      <Badge variant="secondary" className="w-fit">
                        <FileCheck className="mr-1 h-3 w-3" />
                        MSA Organization
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
